import { z } from 'zod';
import { TargetType } from "../combat/types.js";
import { AttributeEnum, CharacterCreationStepEnum, NpcCharacterRelation, NpcPlaceRelation, NpcReputation, Pronouns, SkillEnum } from "../entity/types.js";
import { CharacterDefinitionSchema } from "./characterDef.schema.js";
import { ItemInstanceDTOSchema, EquipmentSlotsSchema } from "./item.schema.js";
export const AttributesSchema = z.object({
    [AttributeEnum.MIGHT]: z.number().int().min(1).max(20),
    [AttributeEnum.AGILITY]: z.number().int().min(1).max(20),
    [AttributeEnum.RESOLVE]: z.number().int().min(1).max(20),
    [AttributeEnum.INTELLECT]: z.number().int().min(1).max(20),
    [AttributeEnum.CHARM]: z.number().int().min(1).max(20),
});
export const SkillsSchema = z.object({
    [SkillEnum.ATHLETICS]: z.number().int().min(0).max(10).optional(),
    [SkillEnum.STEALTH]: z.number().int().min(0).max(10).optional(),
    [SkillEnum.BUSHCRAFT]: z.number().int().min(0).max(10).optional(),
    [SkillEnum.MELEE]: z.number().int().min(0).max(10).optional(),
    [SkillEnum.RANGED]: z.number().int().min(0).max(10).optional(),
    [SkillEnum.SPELLCRAFT]: z.number().int().min(0).max(10).optional(),
    [SkillEnum.LORE]: z.number().int().min(0).max(10).optional(),
    [SkillEnum.CRAFT]: z.number().int().min(0).max(10).optional(),
    [SkillEnum.HEALING]: z.number().int().min(0).max(10).optional(),
    [SkillEnum.GUILE]: z.number().int().min(0).max(10).optional(),
    [SkillEnum.INSIGHT]: z.number().int().min(0).max(10).optional(),
    [SkillEnum.PERCEPTION]: z.number().int().min(0).max(10).optional(),
});
export const TargetSchema = z.object({
    type: z.nativeEnum(TargetType),
    number: z.number().int().min(1),
    range: z.number().int().min(0),
    requiresLos: z.boolean().optional(),
    aoeRadius: z.number().int().min(1).optional(),
});
const AbilityDisplayPositionSchema = z.object({
    x: z.number().int(),
    y: z.number().int(),
});
const AbilityDisplaySpriteAnimationActionSchema = z.object({
    type: z.literal('spriteAnimation'),
    entityId: z.string(),
    animation: z.string(),
});
const AbilityDisplayParticleEffectActionSchema = z.object({
    type: z.literal('particleEffect'),
    entityId: z.string().optional(),
    position: AbilityDisplayPositionSchema.optional(),
    effect: z.string(),
});
const AbilityDisplayAudioClipActionSchema = z.object({
    type: z.literal('audioClip'),
    clip: z.string(),
});
const AbilityDisplayProjectileActionSchema = z.object({
    type: z.literal('projectile'),
    from: z.string(),
    to: z.string(),
    sprite: z.string(),
    toPosition: AbilityDisplayPositionSchema.optional(),
});
const AbilityDisplayAoeExplosionActionSchema = z.object({
    type: z.literal('aoeExplosion'),
    center: z.string(),
    effect: z.string(),
});
const AbilityDisplayActionSchema = z.discriminatedUnion('type', [
    AbilityDisplaySpriteAnimationActionSchema,
    AbilityDisplayParticleEffectActionSchema,
    AbilityDisplayAudioClipActionSchema,
    AbilityDisplayProjectileActionSchema,
    AbilityDisplayAoeExplosionActionSchema,
]);
const AbilityDisplayEventSchema = z.object({
    trigger: z.enum(['start', 'complete', 'miss', 'damage']),
    actions: z.array(AbilityDisplayActionSchema),
});
const AbilityScriptSchema = z.object({
    name: z.string(),
    args: z.record(z.union([z.string(), z.number()])),
});
export const AbilitySchema = z.object({
    id: z.string(),
    key: z.string(), // non-unique key allows for ability overrides
    name: z.string(),
    target: TargetSchema, // Valid target types
    mpCost: z.number().int().min(0),
    script: AbilityScriptSchema,
    displayEvents: z.array(AbilityDisplayEventSchema).optional(),
});
export const BaseEntitySchema = z.object({
    id: z.string(),
    name: z.string(),
    attributes: AttributesSchema,
    skills: SkillsSchema,
    abilities: z.array(z.string()).optional(), // IDs for lookup
    armor: z.number().int().min(0).optional(),
    level: z.number().int().min(1).max(10),
    sprite: z.string().optional(),
    spriteHash: z.string().optional(),
});
export const PersistentCharacterSchema = BaseEntitySchema.extend({
    userId: z.string(),
    currentHP: z.number().int(),
    currentMP: z.number().int()
});
export const AppearanceSelectionSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('color'), hex: z.string() }),
    z.object({ type: z.literal('choice'), choiceId: z.string() }),
    z.object({ type: z.literal('toggle'), enabled: z.boolean(), hex: z.string().optional() }),
]);
export const CharacterAppearanceSchema = z.object({
    selections: z.record(AppearanceSelectionSchema),
});
export { EquipmentSpriteOverridesSchema } from './characterDef.schema.js';
export const MotivationSchema = z.object({
    question: z.string(),
    answer: z.string().optional(),
    digest: z.string().optional()
});
const PronounsSchema = z.nativeEnum(Pronouns);
export const PlayerCharacterSchema = PersistentCharacterSchema.extend({
    type: z.literal("player"),
    creationStep: z.nativeEnum(CharacterCreationStepEnum),
    motivations: z.array(MotivationSchema),
    appearance: CharacterAppearanceSchema.optional(),
    inventory: z.array(ItemInstanceDTOSchema).optional(),
    equipment: EquipmentSlotsSchema.optional(),
    pronouns: PronounsSchema.optional(),
});
// A relationship's `kind` is validated against its `targetType`: character
// relations for npc/player targets, place relations for place targets.
const NpcCharacterRelationshipSchema = z.object({
    targetType: z.enum(['npc', 'player']),
    targetId: z.string(),
    kind: z.nativeEnum(NpcCharacterRelation),
    note: z.string().optional(),
});
const NpcPlaceRelationshipSchema = z.object({
    targetType: z.literal('place'),
    targetId: z.string(),
    kind: z.nativeEnum(NpcPlaceRelation),
    note: z.string().optional(),
});
export const NPCRelationshipSchema = z.union([
    NpcCharacterRelationshipSchema,
    NpcPlaceRelationshipSchema,
]);
export const NonPlayerCharacterSchema = PersistentCharacterSchema.extend({
    type: z.literal("npc"),
    spriteType: z.enum(['composite', 'static']),
    characterDefinition: CharacterDefinitionSchema.optional(),
    motivations: z.array(z.string()),
    lifetimeMemories: z.array(z.string()),
    adventureMemories: z.array(z.string()),
    relationships: z.array(NPCRelationshipSchema),
    playerCharacterId: z.string(),
    playerExposedDescription: z.string(),
    activeGameId: z.string().optional(),
    reputation: z.nativeEnum(NpcReputation),
    pronouns: PronounsSchema.optional(),
    originEnemyDefId: z.string().optional(),
});
// Public-facing NPC schema — omits private fields (motivations, memories, relationships, etc.)
// Use this for all client API responses; never send the full NonPlayerCharacterSchema to the client.
export const NpcPublicSchema = NonPlayerCharacterSchema.pick({
    id: true,
    name: true,
    type: true,
    level: true,
    attributes: true,
    skills: true,
    armor: true,
    currentHP: true,
    currentMP: true,
    sprite: true,
    spriteHash: true,
    spriteType: true,
    characterDefinition: true,
    playerExposedDescription: true,
    pronouns: true,
});
export const CharacterSchema = z.discriminatedUnion("type", [
    PlayerCharacterSchema,
    NonPlayerCharacterSchema,
]);
/**
 * Character creation payloads
 */
export const CharacterPatchRequestSchema = z.object({
    id: z.string(),
    motivations: z.array(MotivationSchema).optional(),
    appearance: CharacterAppearanceSchema.optional(),
    attributes: AttributesSchema.optional(),
    skills: SkillsSchema.optional(),
    name: z.string().min(2).max(32).optional(),
    pronouns: PronounsSchema.optional(),
}).refine((obj) => !!(obj.motivations || obj.appearance || obj.attributes || obj.skills || obj.name || obj.pronouns), {
    message: 'Must provide one of motivations, appearance, attributes, skills, name, or pronouns'
});
export const CharacterPatchResponseSchema = z.object({
    character: PlayerCharacterSchema.optional()
});
